import type { BaseSyntheticEvent, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { FormField } from '@elfsight/widget-form-builder/widget';
import { FieldComponent as Field } from '@elfsight/widget-form-builder/widget';
import type { TextControlBaseInstance } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import type { SubmitFuncType } from '../../form-controller-context';
import { useFormController } from '../../form-controller-context';
import { EMAIL_ID } from './constants';

interface FormLayoutProps {
  formSettings: any;
  fields: FormField[];
  onSubmit?(e: BaseSyntheticEvent): Promise<void>;
  isMobile?: boolean;
  spin: () => void;
}

export function FormLayout({
  onSubmit,
  fields,
  isMobile,
  spin
}: FormLayoutProps) {
  const { accentColor, editMode, emailFieldErrorColor } = useSettings();
  const { focusCallbackRef, submitCallbackRef } = useFormController();

  const fieldProps = {
    formSettings: {
      fieldColor: 'rgba(17, 17, 17, 1)',
      fieldActiveColor: accentColor
    },
    fieldPadding: 0,
    fieldSpacing: 0,
    fieldBorderType: 'outline',
    fieldBorderColor: 'rgba(17, 17, 17, 0.3)',
    fieldStylesOnFocus: {
      boxShadow: `inset 1px 1px 0px ${accentColor}, inset -1px -1px 0px ${accentColor}`
    },
    errorColor: emailFieldErrorColor,
    fieldStylesOnError: {
      boxShadow: `inset 1px 1px 0px ${emailFieldErrorColor}, inset -1px -1px 0px ${emailFieldErrorColor}`
    },
    fieldBorderRadius: 4,
    fieldFillColor: '#fff',
    placeholderColor: 'rgba(17, 17, 17, 0.5)',
    fieldFontStyle: {
      fontSize: isMobile ? 16 : 18
    }
  };

  const focusedFieldRef = useRef<TextControlBaseInstance>(null);
  const [focusedField, setFocusedField] = useState(EMAIL_ID);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && onSubmit) {
      spin?.();
    }
  };

  useEffect(() => {
    if (editMode) return;
    const animationFrame = requestAnimationFrame(() => {
      focusedFieldRef.current?.focusInput();
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [focusedField]);

  useEffect(() => {
    submitCallbackRef.current = onSubmit as SubmitFuncType;
  }, [onSubmit]);

  useEffect(() => {
    focusCallbackRef.current = focusedFieldRef?.current?.focusInput;
  }, [focusCallbackRef.current]);

  return (
    <Container onKeyDown={handleKeyDown}>
      {fields.map((field: FormField) => (
        <Field
          ref={field.id === focusedField ? focusedFieldRef : undefined}
          onFocus={() => setFocusedField(field.id)}
          onBlur={() => setFocusedField('')}
          key={field.id}
          field={field}
          {...fieldProps}
        />
      ))}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
